import { Video } from "../common/video/models/Video";
import { VideoCaption } from "../common/video/models/VideoCaption";
import { VideoFilter } from "../common/video/models/VideoFilter";
import { Slide } from "../models/slides";
import { UserRole } from "../models/user";
import { FilterTag } from "../viewer/search/Search";
import { isNullorUndefined } from "./ObjectUtils";

export const roles = ["管理者", "編集者", "一般"];

// User権限判定
export function isUser(roles: UserRole[] | undefined) {
    return roles?.length === 0;
}

// Admin権限判定
export function isAdmin(roles: UserRole[] | undefined) {
    return !!roles?.some((role) => role.role === "ADMIN");
}

// Editor権限判定
export function isEditor(roles: UserRole[] | undefined) {
    return !!roles?.some((role) => role.role === "EDITOR");
}

// Profesional権限判定
export function isProfessional(roles: UserRole[] | undefined) {
    return !!roles?.some((role) => role.role === "PRESENTER");
}

// Guest権限判定
export function isGuest(roles: UserRole[] | undefined) {
    return !!roles?.some((role) => role.role === "GUEST");
}

// UNAVAILABLE権限判定
export function isUnavailable(roles: UserRole[] | undefined) {
    return !!roles?.some((role) => role.role === "UNAVAILABLE");
}

// API実行エラー判定
export function isAxiosError(error: any) {
    return isNullorUndefined(error) ? false : error.isAxiosError;
}

// 指定範囲内の乱数を生成する
export function getRandomInt(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1) + min);
}

// パラメータをメッセージに設定する
export function setMessageArgs(str: string, ...args: string[]) {
    let message = str;
    for (let i = 0; i < args.length; i++) {
        message = message.replace(`{${i}}`, args[i]);
    }

    return message;
}

// ビデオ処理ステータス判定
export function getVideoStatus(status: string) {
    switch (status.toUpperCase()) {
        case "CREATED":
            return "Video処理中";
        case "STORED":
        case "DATA_EXTRACTED":
            return "STT生成中";
        case "SUBTITLES":
            return "サムネイルアップロード中";
        case "THUMBNAILS":
            return "資料アップロード中";
        case "FAILED":
            return "処理失敗";
        case "COMPLETED":
            return "処理完了";
        case "":
            return "準備中";
        default:
            return "ステータス不明";
    }
}

// ビデオ処理進捗値判定
export function getStatusProgress(status: string) {
    switch (status.toUpperCase()) {
        case "CREATED":
            return 20;
        case "STORED":
            return 50;
        case "DATA_EXTRACTED":
            return 60;
        case "SUBTITLES":
            return 70;
        case "THUMBNAILS":
            return 80;
        case "FAILED":
        case "COMPLETED":
            return 100;
        default:
            return 0;
    }
}

// CaptionをVTTファイルに変換する
export function ConvertCaptionToVtt(fileName: string, captions: VideoCaption[], language?: "ja") {
    let content = "WEBVTT\n\n";
    captions.map((caption) => {
        content +=
            ConvertMsToTimeWithDotMs(caption.startMillis) +
            " --> " +
            ConvertMsToTimeWithDotMs(caption.endMillis) +
            "\n" +
            caption.transcript +
            "\n\n";
        return null;
    });
    return window.URL.createObjectURL(
        new File([content], fileName + "." + language + ".vtt", { type: "text/vtt" })
    );
}
// ミリ秒 ⇒ hh:mi:ss.zzz
export function ConvertMsToTimeWithDotMs(millis: number) {
    return stringifyVideoTimeWithColons(millis) + "." + ("000" + millis).slice(-3);
}

export function stringifyVideoTimeWithColons(millis: number, alwaysShowHours?: true) {
    const [hours, minutes, seconds] = millisToHoursMinutesSeconds(millis);

    const hourString = hours < 10 ? `0${hours}` : `${minutes}`;
    const minuteString = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const secondString = seconds < 10 ? `0${seconds}` : `${seconds}`;

    const showHours = hours > 0 || alwaysShowHours;

    return showHours
        ? [hourString, minuteString, secondString].join(":")
        : [minuteString, secondString].join(":");
}

const MILLIS_PER_SECOND = 1000;
const MILLIS_PER_MINUTE = MILLIS_PER_SECOND * 60;
const MILLIS_PER_HOUR = MILLIS_PER_MINUTE * 60;

function millisToHoursMinutesSeconds(millis: number) {
    const wholeHours = Math.floor(millis / MILLIS_PER_HOUR);

    const minuteMillis = millis - wholeHours * MILLIS_PER_HOUR;
    const wholeMinutes = Math.floor(minuteMillis / MILLIS_PER_MINUTE);

    const secondMillis = minuteMillis - wholeMinutes * MILLIS_PER_MINUTE;
    const wholeSeconds = Math.floor(secondMillis / MILLIS_PER_SECOND);

    return [wholeHours, wholeMinutes, wholeSeconds];
}

// ビデオホバーで管理者機能アイコン表示
export const ShowVideoAdminBtn = () => {
    const HoverButton = document.getElementById("videoMenu");
    const videoAdminBtn = document.getElementById("videoAdminBtn");

    if (HoverButton) {
        HoverButton.style.visibility = "visible";
    }
    if (videoAdminBtn) {
        videoAdminBtn.style.visibility = "visible";
    }
};

// ビデオホバーで管理者機能アイコン非表示
export const HideVideoAdminBtn = () => {
    const HoverButton = document.getElementById("videoMenu");
    const videoAdminBtn = document.getElementById("videoAdminBtn");

    if (HoverButton) {
        HoverButton.style.visibility = "hidden";
    }

    if (videoAdminBtn) {
        videoAdminBtn.style.visibility = "hidden";
    }
};
// ビデオリストのフィルタリング
export function filterVideoListFunction(videos: Video[], filter: VideoFilter) {
    const filterList = videos
        .filter((video) => isMatchPresenter(video.presenter, filter.professional))
        .filter((video) => isMatchTag(video.tags, filter.tag));
    return { videos: filterList, tags: filterVideoTagList(filterList, filter) };
}

// コンテンツリストのプレゼンターフィルター
function isMatchPresenter(contentsPresenter: string, professional: string[]) {
    let flag = false;
    if (professional.length === 0) {
        flag = true;
    } else {
        if (professional[0] === contentsPresenter) {
            flag = true;
        }
    }
    return flag;
}

// コンテンツリストのタグフィルター
function isMatchTag(contentTags: string[], tags: string[]) {
    if (tags.length === 0) {
        return true;
    } else {
        return tags.every((filterTag) => contentTags.includes(filterTag));
    }
}

// スライドリストのフィルタリング
export function filterSlideListFunction(slides: Slide[], filter: VideoFilter) {
    const filterList = slides
        .filter((slide) => isMatchPresenter(slide.presenter, filter.professional))
        .filter((slide) => isMatchTag(slide.tags, filter.tag));
    return { slides: filterList, tags: filterSlideTagList(filterList, filter) };
}

// ビデオリストフィルタリング時のタグリストの作成
export function filterVideoTagList(videos: Video[], filter?: VideoFilter) {
    const videoAllTags: string[] = [];

    // ビデオリストのタグを取り出す
    videos.forEach((video) =>
        video.tags.forEach((tag) => {
            videoAllTags.push(tag);
        })
    );

    // 取り出したビデオリストのタグを重複がないようにTag[]の形に変換する
    const tempTags: FilterTag[] = videoAllTags.map((tag) => ({
        tag: tag,
        tagCount: videoAllTags.filter((videoTag) => videoTag === tag).length,
    }));

    // フィルタされてるタグがリストにない場合、リストに追加する。
    if (filter) {
        filter.tag.forEach((tag) => {
            if (!videoAllTags.includes(tag)) {
                tempTags.push({
                    tag: tag,
                    tagCount: 0,
                });
            }
        });
    }

    return Array.from(new Map(tempTags.map((tag) => [tag.tag, tag])).values());
}

// スライドリストフィルタリング時のタグリストの作成
export function filterSlideTagList(slides: Slide[], filter?: VideoFilter) {
    const slideAllTags: string[] = [];
    // スライドリストのタグを取り出す
    slides.forEach((slide) =>
        slide.tags.forEach((tag) => {
            slideAllTags.push(tag);
        })
    );

    // 取り出したスライドリストのタグを重複がないようにTag[]の形に変換する
    const tempTags: FilterTag[] = slideAllTags.map((tag) => ({
        tag: tag,
        tagCount: slideAllTags.filter((slideTag) => slideTag === tag).length,
    }));

    // フィルタされてるタグがリストにない場合、リストに追加する。
    if (filter) {
        filter.tag.forEach((tag) => {
            if (!slideAllTags.includes(tag)) {
                tempTags.push({
                    tag: tag,
                    tagCount: 0,
                });
            }
        });
    }

    return Array.from(new Map(tempTags.map((tag) => [tag.tag, tag])).values());
}
